import { is } from 'utils/helpers';
import { type Result, toResult } from 'utils/result';

import {
	type FetchError,
	type FetchInput,
	type FetchResponse,
	fetchResponse,
	type RequestOptions,
	unwrapResponse,
} from './core';
import {
	getCloudflareAccessHeaders,
	getForwardedForHeader,
	mergeHeaders,
	toHeaderRecord,
} from './headers';

/**
 * Headers as they arrive on a Node request, where a value can repeat or be
 * absent, matching `IncomingHttpHeaders`.
 */
export type ServerHeadersInit =
	| HeadersInit
	| Record<string, string | string[] | undefined>;

/**
 * Convert server headers to a plain object.
 *
 * Absent values are dropped and a repeated one keeps its first value, so
 * headers taken straight off a Node request can be passed on as they are.
 * Keys are left as they are, mergeHeaders lowercases them.
 */
function toServerHeaderRecord(
	headers: ServerHeadersInit | undefined,
): Record<string, string> {
	if (!headers || is.array(headers) || is.instance(headers, Headers)) {
		return toHeaderRecord(headers);
	}

	const record: Record<string, string> = {};
	for (const [key, value] of Object.entries(headers)) {
		const singleValue = is.array(value) ? value[0] : value;
		if (is.defined(singleValue)) {
			record[key] = singleValue;
		}
	}
	return record;
}

export interface ServerRequestOptions extends Omit<RequestOptions, 'headers'> {
	/** Full Authorization header value, e.g. `Bearer abc` or `Basic abc`. */
	auth?: string;
	/** Headers straight off a Node request are accepted as they are. */
	headers?: ServerHeadersInit;
	/** Client IP to pass on in the X-Forwarded-For header. */
	userIp?: string;
}

// The core option types are constrained to its own RequestOptions, which
// these headers are wider than, so mirror them for the server options.
type AllowEmptyServerOptions = ServerRequestOptions & { allowEmpty: true };
type DisallowEmptyServerOptions = ServerRequestOptions & { allowEmpty?: false };
type ServerResponseOptions = Omit<ServerRequestOptions, 'allowEmpty'>;

function getServerHeaders({
	auth,
	userIp,
}: Pick<ServerRequestOptions, 'auth' | 'userIp'>) {
	return {
		...getCloudflareAccessHeaders(),
		// TODO: Consider warn/log/event when this header is missing/empty.
		...getForwardedForHeader(userIp),
		...(auth && { Authorization: auth }),
	};
}

/**
 * Do a server-to-server request and get the parsed response, status and
 * headers.
 *
 * Adds Cloudflare Access and forwarded client IP headers. Unlike the API
 * client there is no token lock or 401 handling, callers pass any
 * authorization themselves.
 */
export function fetchServerResponse<T = unknown>(
	input: FetchInput,
	{ auth, headers, userIp, ...options }: ServerResponseOptions = {},
): Promise<FetchResponse<T>> {
	return fetchResponse<T>(input, {
		...options,
		headers: mergeHeaders(
			getServerHeaders({ auth, userIp }),
			toServerHeaderRecord(headers),
		),
	});
}

/**
 * Do a server-to-server request and get the parsed response, throwing on a
 * non-ok or empty response.
 */
export async function fetchServerData<T = unknown>(
	input: FetchInput,
	options: AllowEmptyServerOptions,
): Promise<T | undefined>;
export async function fetchServerData<T = unknown>(
	input: FetchInput,
	options?: DisallowEmptyServerOptions,
): Promise<T>;
export async function fetchServerData<T = unknown>(
	input: FetchInput,
	options?: ServerRequestOptions,
): Promise<T | undefined>;
export async function fetchServerData<T = unknown>(
	input: FetchInput,
	options?: ServerRequestOptions,
): Promise<T | undefined> {
	const { allowEmpty, ...requestOptions } = options ?? {};
	return unwrapResponse(
		await fetchServerResponse<T>(input, requestOptions),
		allowEmpty,
	);
}

/** Do a server-to-server request and get a result type with the data or error. */
export async function fetchServerResult<T = unknown>(
	input: FetchInput,
	options: AllowEmptyServerOptions,
): Promise<Result<T | undefined, FetchError>>;
export async function fetchServerResult<T = unknown>(
	input: FetchInput,
	options?: DisallowEmptyServerOptions,
): Promise<Result<T, FetchError>>;
export async function fetchServerResult<T = unknown>(
	input: FetchInput,
	options?: ServerRequestOptions,
): Promise<Result<T | undefined, FetchError>>;
export async function fetchServerResult<T = unknown>(
	input: FetchInput,
	options?: ServerRequestOptions,
): Promise<Result<T | undefined, FetchError>> {
	return toResult(fetchServerData<T>(input, options));
}
